import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";

import styles from "./ReactivationNotice.module.css";

/** How long the notice stays up before dismissing itself. */
const AUTO_DISMISS_MS = 8000;

/**
 * Global toast shown once after a sign-in that reactivated a deactivated account, confirming that
 * the account is back and every non-deleted tree has been restored. Mounted app-wide (it follows
 * whatever page the sign-in redirected to) and auto-dismisses.
 */
const ReactivationNotice = () => {
	const { t } = useTranslation("common");
	const { dismissReactivationNotice, reactivationNoticeVisible } = useSession();

	useEffect(() => {
		if (!reactivationNoticeVisible) {
			return undefined;
		}

		const timer = window.setTimeout(dismissReactivationNotice, AUTO_DISMISS_MS);

		return () => window.clearTimeout(timer);
	}, [dismissReactivationNotice, reactivationNoticeVisible]);

	if (!reactivationNoticeVisible) {
		return null;
	}

	return (
		<div aria-live="polite" className={styles.toast} role="status">
			<span aria-hidden="true" className={styles.icon}>
				<svg fill="none" focusable="false" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
					<path d="m7.75 12.25 2.6 2.6 5.9-6.1" />
				</svg>
			</span>
			<div className={styles.text}>
				<span className={styles.title}>{t("reactivatedTitle")}</span>
				<span className={styles.message}>{t("reactivatedMessage")}</span>
			</div>
			<button
				aria-label={t("close")}
				className={styles.closeButton}
				onClick={dismissReactivationNotice}
				type="button">
				<svg
					aria-hidden="true"
					fill="none"
					focusable="false"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg">
					<path d="m6 6 12 12M18 6 6 18" />
				</svg>
			</button>
		</div>
	);
};

export default ReactivationNotice;
